
import { useState } from 'react';

export default function BusinessSetup() {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedGuide, setSelectedGuide] = useState<string | null>(null);

  const businessTypes = [
    {
      id: 'registration',
      title: '企业开办',
      description: '通过一网通平台全程网办，1-2个工作日完成',
      icon: 'ri-building-2-line',
      timeframe: '1-2个工作日',
      materials: ['身份证正反面', '注册地址证明', '公司基本信息'],
      process: ['实名登录', '填写信息', '上传材料', '电子签名', '提交申请', '领取证照']
    },
    {
      id: 'seal-record',
      title: '刻章备案',
      description: '企业印章刻制及公安备案，确保印章合法有效',
      icon: 'ri-stamp-line',
      timeframe: '1个工作日',
      materials: ['营业执照', '法定代表人身份证', '刻章申请书'],
      process: ['选择刻章点', '提交材料', '刻制印章', '公安备案', '领取印章']
    },
    {
      id: 'bank-account',
      title: '银行开户',
      description: '企业基本存款账户开立，办理日常资金收付业务',
      icon: 'ri-bank-card-line',
      timeframe: '3-5个工作日',
      materials: ['营业执照', '公章财务章', '法人身份证', '开户许可证申请'],
      process: ['选择银行', '预约开户', '现场办理', '账户激活', '获取开户许可']
    },
    {
      id: 'tax-registration',
      title: '税务报到',
      description: '税务登记及纳税人身份确认，建立税务档案',
      icon: 'ri-file-list-3-line',
      timeframe: '1-3个工作日',
      materials: ['营业执照', '税务登记表', '银行开户许可证', '财务制度'],
      process: ['税务登记', '票种核定', '申领发票', '确定申报期', '完成报到']
    },
    {
      id: 'change',
      title: '变更登记',
      description: '法人、地址、经营范围等变更业务办理',
      icon: 'ri-edit-line',
      timeframe: '5个工作日',
      materials: ['公司登记申请书', '股东会决议', '营业执照正副本'],
      process: ['准备材料', '网上申请', '窗口审核', '核准变更', '领取新照']
    },
    {
      id: 'simple-cancel',
      title: '简易注销',
      description: '未开业或无债权债务企业的快速注销',
      icon: 'ri-close-circle-line',
      timeframe: '20天公示期',
      materials: ['注销登记申请书', '全体投资人承诺书', '营业执照'],
      process: ['填报承诺书', '公示20天', '提交申请', '审核通过', '完成注销']
    },
    {
      id: 'general-cancel',
      title: '一般注销',
      description: '已开展经营或有债权债务企业的注销',
      icon: 'ri-file-list-line',
      timeframe: '2-3个月',
      materials: ['清算报告', '股东会决议', '清税证明', '公告样张'],
      process: ['成立清算组', '债权人公告', '税务注销', '提交申请', '核准注销']
    }
  ];

  const platformAccess = [
    {
      type: 'PC端',
      icon: 'ri-computer-line',
      color: 'bg-blue-500',
      description: '通过广东政务服务网或广州市市场监督管理局官网找到入口',
      features: ['功能全面', '操作便捷', '支持批量操作']
    },
    {
      type: '移动端',
      icon: 'ri-smartphone-line',
      color: 'bg-green-500',
      description: '微信搜索"广州市开办企业一网通"小程序',
      features: ['随时随地', '扫码签名', '消息提醒']
    }
  ];

  const keyPoints = [
    {
      title: '信息准确一致',
      description: '确保企业名称、统一社会信用代码、法定代表人等信息与工商登记信息完全一致',
      icon: 'ri-check-double-line',
      color: 'text-green-600'
    },
    {
      title: '电子签名与材料',
      description: '全程电子化流程需相关人员进行电子签名，提前备好所需材料的清晰电子版',
      icon: 'ri-file-edit-line',
      color: 'text-blue-600'
    },
    {
      title: '政策动态关注',
      description: '商事登记政策时常更新，办理前查询最新办事指南',
      icon: 'ri-notification-line',
      color: 'text-orange-600'
    },
    {
      title: '复杂情况求助',
      description: '如企业情况复杂，建议咨询专业的工商财税代理机构或律师',
      icon: 'ri-customer-service-line',
      color: 'text-purple-600'
    }
  ];

  const detailedGuides = {
    'registration': {
      title: '企业开办详细指南',
      sections: [
        {
          title: '适用条件',
          icon: 'ri-checkbox-line',
          content: [
            '新设立的有限责任公司、股份有限公司等各类企业',
            '申请人具备完全民事行为能力',
            '有合法的企业名称和经营场所',
            '符合国家法律法规和产业政策要求'
          ]
        },
        {
          title: '详细材料清单',
          icon: 'ri-file-list-3-line',
          content: [
            '【人员信息】所有投资人、法定代表人、董事、监事、经理身份证件及联系方式',
            '【地址材料】房屋租赁合同、房产证明、产权人身份证明等',
            '【企业信息】公司章程、股东会决议、企业名称预核准通知书（如有）',
            '【特殊行业】涉及前置审批的需提供相关许可证件'
          ]
        },
        {
          title: '操作步骤详解',
          icon: 'ri-route-line',
          content: [
            '第1步：登录平台 - 访问"广州市开办企业一网通平台"，使用广东CA或微信扫码登录',
            '第2步：企业核名 - 输入拟定企业名称，系统自动核查重名情况',
            '第3步：填写信息 - 完善企业基本信息、投资人信息、经营范围等',
            '第4步：上传材料 - 按要求上传各类证明材料的电子版',
            '第5步：电子签名 - 相关当事人使用CA证书或手机短信验证进行电子签名',
            '第6步：提交审核 - 确认信息无误后提交，等待市场监管部门审核',
            '第7步：获取执照 - 审核通过后可选择现场领取或EMS邮寄服务'
          ]
        },
        {
          title: '注意事项',
          icon: 'ri-alert-line',
          content: [
            '企业名称建议准备3-5个备选，避免重名导致延误',
            '注册地址必须真实有效，后续可能面临实地核查',
            '经营范围要准确填写，超范围经营可能面临处罚',
            '电子签名环节所有相关人员必须本人操作，不得代签'
          ]
        },
        {
          title: '常见问题',
          icon: 'ri-question-line',
          content: [
            'Q: 审核需要多长时间？A: 一般1-3个工作日，材料齐全且符合要求的当天即可完成',
            'Q: 可以使用住宅地址注册吗？A: 部分区域允许，需要提供相关证明和业主同意书',
            'Q: 注册资本需要实际缴纳吗？A: 实行认缴制，无需立即实缴，但需在章程规定期限内到位',
            'Q: 外地人员可以在广州注册企业吗？A: 可以，但法定代表人需要进行实名认证'
          ]
        }
      ]
    },
    'seal-record': {
      title: '刻章备案详细指南',
      sections: [
        {
          title: '刻章类型及用途',
          icon: 'ri-stamp-line',
          content: [
            '【公章】企业法人章，用于对外正式文件、合同签署等重要事务',
            '【财务章】用于财务结算、银行业务、开具发票等财务相关事宜',
            '【法定代表人章】简称法人章，用于银行开户、重要合同等特定场合',
            '【发票专用章】专门用于开具各类发票，是税务必需印章',
            '【合同专用章】专门用于合同签署，部分企业会刻制以规范合同管理'
          ]
        },
        {
          title: '办理流程',
          icon: 'ri-route-line',
          content: [
            '第1步：选择刻章点 - 选择公安部门备案的正规刻章企业',
            '第2步：提交材料 - 提供营业执照原件及复印件、法人身份证等',
            '第3步：填写申请 - 填写《刻制印章申请书》，明确刻章类型和数量',
            '第4步：刻制印章 - 刻章企业按规范要求刻制各类印章',
            '第5步：公安备案 - 刻章企业代为向公安部门申报备案',
            '第6步：领取印章 - 验收印章质量，领取印章及印鉴卡'
          ]
        },
        {
          title: '所需材料',
          icon: 'ri-file-text-line',
          content: [
            '营业执照正本原件及复印件（加盖公章）',
            '法定代表人身份证原件及复印件',
            '《刻制印章申请书》（现场填写或下载填写）',
            '授权委托书（非法人本人办理时需要）',
            '经办人身份证原件及复印件（委托办理时）'
          ]
        },
        {
          title: '费用标准',
          icon: 'ri-money-dollar-circle-line',
          content: [
            '公章：120-200元/枚（根据材质不同价格有差异）',
            '财务章：80-150元/枚',
            '法定代表人章：80-150元/枚',
            '发票专用章：80-150元/枚',
            '合同专用章：80-150元/枚（可选）'
          ]
        },
        {
          title: '重要提醒',
          icon: 'ri-alarm-warning-line',
          content: [
            '必须选择公安部门备案的正规刻章企业，避免使用非法刻章服务',
            '印章刻制完成后要妥善保管，建立印章使用登记制度',
            '如印章遗失或损坏，应立即到公安部门报案并申请补刻',
            '企业注销时，所有印章应到公安机关指定地点销毁'
          ]
        }
      ]
    },
    'bank-account': {
      title: '银行开户详细指南',
      sections: [
        {
          title: '账户类型说明',
          icon: 'ri-bank-line',
          content: [
            '【基本存款账户】企业的主要账户，用于日常转账结算和现金收付',
            '【一般存款账户】在基本账户之外开立的账户，用于专项资金管理',
            '【专用存款账户】用于特定用途的资金管理，如社保、住房公积金等',
            '【临时存款账户】因临时需要开立的账户，有效期最长2年'
          ]
        },
        {
          title: '开户银行选择',
          icon: 'ri-building-line',
          content: [
            '【国有银行】工商银行、农业银行、中国银行、建设银行等，网点多、服务稳定',
            '【股份制银行】招商银行、浦发银行、中信银行等，服务相对灵活',
            '【地方银行】广州银行、广发银行等，对本地企业可能有优惠政策',
            '选择原则：考虑服务费用、办事效率、网点便利性和后续服务质量'
          ]
        },
        {
          title: '开户流程详解',
          icon: 'ri-flow-chart',
          content: [
            '第1步：预约开户 - 电话或网上预约，确定开户时间和所需材料',
            '第2步：准备材料 - 按银行要求准备完整的开户材料清单',
            '第3步：现场办理 - 法定代表人携带材料到银行网点办理',
            '第4步：填写表格 - 填写《开立银行结算账户申请书》等表格',
            '第5步：预留印鉴 - 在银行预留公章和法人章印鉴',
            '第6步：等待审批 - 银行审核材料并向人民银行申请账户核准',
            '第7步：账户激活 - 获得开户许可证后，账户正式启用'
          ]
        },
        {
          title: '所需材料清单',
          icon: 'ri-file-list-2-line',
          content: [
            '营业执照正本原件及复印件（加盖公章）',
            '组织机构代码证原件及复印件（三证合一后不需要）',
            '税务登记证原件及复印件（三证合一后不需要）',
            '法定代表人身份证原件及复印件',
            '公章、财务专用章、法定代表人名章',
            '《开立银行结算账户申请书》（银行提供）'
          ]
        },
        {
          title: '费用说明',
          icon: 'ri-wallet-line',
          content: [
            '开户手续费：200-500元不等（各银行标准不同）',
            '账户管理费：一般为每月10-50元',
            '网银服务费：年费200-800元不等',
            '对公转账手续费：按转账金额和方式收取',
            '部分银行对新开企业有优惠政策，可享受费用减免'
          ]
        },
        {
          title: '注意事项',
          icon: 'ri-information-line',
          content: [
            '基本存款账户只能开立一个，选择银行要慎重考虑',
            '开户必须由法定代表人本人办理，不可委托他人',
            '预留印鉴要妥善保管，印鉴变更需要到银行办理手续',
            '账户开立后要及时办理网银、手机银行等电子银行业务'
          ]
        }
      ]
    },
    'tax-registration': {
      title: '税务报到详细指南',
      sections: [
        {
          title: '税务报到意义',
          icon: 'ri-government-line',
          content: [
            '确认纳税人身份，建立企业税务档案',
            '核定企业适用的税种和税率',
            '确定纳税申报期限和申报方式',
            '申领发票，开展正常经营活动',
            '享受相关税收优惠政策'
          ]
        },
        {
          title: '报到时限要求',
          icon: 'ri-time-line',
          content: [
            '新设立企业应在领取营业执照之日起30日内办理税务报到',
            '逾期未报到可能面临罚款，影响企业信用记录',
            '建议在银行开户完成后立即办理税务报到',
            '部分区域提供"套餐服务"，可与工商登记同步办理'
          ]
        },
        {
          title: '办理流程步骤',
          icon: 'ri-route-line',
          content: [
            '第1步：准备材料 - 收集营业执照、银行开户许可证等必需材料',
            '第2步：登录系统 - 进入广东省电子税务局进行网上税务报到',
            '第3步：填写信息 - 完善企业基本信息、财务负责人信息等',
            '第4步：上传资料 - 按要求上传相关证件的电子版',
            '第5步：税种认定 - 根据经营范围确定适用税种',
            '第6步：票种核定 - 申请需要使用的发票种类和开票限额',
            '第7步：完成报到 - 获得税务登记确认，可正常开展税务活动'
          ]
        },
        {
          title: '所需材料详单',
          icon: 'ri-file-copy-line',
          content: [
            '营业执照副本原件及复印件',
            '银行开户许可证原件及复印件',
            '法定代表人身份证原件及复印件',
            '财务负责人身份证原件及复印件及会计从业资格证',
            '公司章程复印件（加盖公章）',
            '房屋租赁合同或房产证明复印件',
            '《税务登记表》（可网上填写）'
          ]
        },
        {
          title: '税种核定说明',
          icon: 'ri-calculator-line',
          content: [
            '【增值税】一般纳税人税率13%、9%、6%等，小规模纳税人征收率3%',
            '【企业所得税】一般税率25%，小微企业可享受优惠税率',
            '【个人所得税】代扣代缴员工个人所得税',
            '【印花税】合同、账簿等按规定税率缴纳',
            '【城市维护建设税】按增值税、营业税之和的7%计征',
            '【教育费附加】按增值税、营业税之和的3%计征'
          ]
        },
        {
          title: '申报期限确定',
          icon: 'ri-calendar-line',
          content: [
            '【增值税】小规模纳税人按季申报，一般纳税人按月申报',
            '【企业所得税】按季预缴，年度汇算清缴',
            '【个人所得税】按月代扣代缴，次月15日前申报',
            '【其他税种】根据税种特点确定申报期限',
            '申报期限遇节假日顺延，要关注税务局具体通知'
          ]
        }
      ]
    },
    'change': {
      title: '变更登记详细指南',
      sections: [
        {
          title: '适用范围',
          icon: 'ri-edit-box-line',
          content: [
            '企业名称变更：因业务发展需要更改企业名称',
            '法定代表人变更：法定代表人职务调整或人员更换',
            '注册地址变更：经营场所迁移或地址信息变化',
            '经营范围变更：增加或减少经营项目',
            '注册资本变更：增资或减资',
            '股权结构变更：股东变更或股权比例调整'
          ]
        },
        {
          title: '分类办理流程',
          icon: 'ri-flow-chart',
          content: [
            '【法定代表人变更】需要：股东会决议、新法定代表人身份证明、任职文件',
            '【地址变更】需要：新地址证明材料、股东会决议、章程修正案',
            '【经营范围变更】需要：股东会决议、章程修正案，涉及许可项目需前置审批',
            '【注册资本变更】需要：股东会决议、验资报告（增资）或债务清偿证明（减资）'
          ]
        },
        {
          title: '网上办理步骤',
          icon: 'ri-smartphone-line',
          content: [
            '登录广东政务服务网企业专区',
            '选择"公司变更登记"事项',
            '填写《公司登记（备案）申请书》',
            '上传相关证明文件电子版',
            '法定代表人进行电子签名确认',
            '提交申请等待审核',
            '审核通过后领取新的营业执照'
          ]
        },
        {
          title: '特殊提醒',
          icon: 'ri-alarm-warning-line',
          content: [
            '变更事项涉及许可经营项目的，需先办理许可证变更',
            '地址跨区变更需要到新地址所在区市场监管局办理',
            '减少注册资本的需要在报纸上公告45天',
            '变更完成后及时到税务、银行等部门办理相应变更手续'
          ]
        }
      ]
    },
    'simple-cancel': {
      title: '简易注销详细指南',
      sections: [
        {
          title: '适用条件（需同时满足）',
          icon: 'ri-checkbox-circle-line',
          content: [
            '领取营业执照后未开展经营活动（未开业）',
            '申请注销登记前未发生债权债务或已将债权债务清算完结',
            '未被列入企业经营异常名录或严重违法失信企业名单',
            '股权（投资权益）未被冻结、出质或动产抵押等',
            '企业类型为有限责任公司、非公司企业法人、个人独资企业、合伙企业'
          ]
        },
        {
          title: '办理流程',
          icon: 'ri-git-branch-line',
          content: [
            '第1步：登录国家企业信用信息公示系统（广东）',
            '第2步：填报《全体投资人承诺书》进行简易注销公示',
            '第3步：公示期满20天，无异议后进入注销申请阶段',
            '第4步：登录广州市开办企业一网通平台提交注销申请',
            '第5步：上传《企业注销登记申请书》等材料',
            '第6步：市场监管部门审核（3个工作日内）',
            '第7步：核准注销，缴回营业执照正副本'
          ]
        },
        {
          title: '所需材料',
          icon: 'ri-file-text-line',
          content: [
            '《企业注销登记申请书》（法定代表人签署）',
            '《全体投资人承诺书》（通过公示系统在线填写）',
            '营业执照正副本',
            '税务部门出具的《清税证明》',
            '公章（注销后需销毁）'
          ]
        },
        {
          title: '重要提醒',
          icon: 'ri-information-line',
          content: [
            '公示期间如有异议，将终止简易注销程序，需按一般注销程序办理',
            '简易注销不适用于存在债权债务纠纷的企业',
            '注销前必须完成税务注销，取得《清税证明》',
            '公示信息必须真实，虚假公示将承担法律责任'
          ]
        }
      ]
    },
    'general-cancel': {
      title: '一般注销详细指南',
      sections: [
        {
          title: '适用情况',
          icon: 'ri-bookmark-line',
          content: [
            '已开展经营活动的企业',
            '存在债权债务关系的企业',
            '被列入经营异常名录的企业',
            '简易注销不适用的其他情况',
            '股东决议解散的企业',
            '法院裁定强制清算的企业'
          ]
        },
        {
          title: '清算程序',
          icon: 'ri-organization-chart',
          content: [
            '成立清算组：股东会决议成立，自行清算或委托专业机构',
            '清算组备案：15日内向市场监管部门备案清算组信息',
            '债权人公告：在国家企业信用信息公示系统公告45天',
            '清理债权债务：收回债权，清偿债务，处理剩余财产',
            '制作清算报告：详细记录清算过程和结果',
            '清算组确认：全体清算组成员签署清算报告'
          ]
        },
        {
          title: '税务注销',
          icon: 'ri-calculator-line',
          content: [
            '办理税务注销前必须完成以下事项：',
            '结清应纳税款、滞纳金、罚款',
            '缴销发票、税务登记证件和其他税务证件',
            '完成税务检查（必要时）',
            '取得《清税证明》或《税务事项通知书》'
          ]
        },
        {
          title: '工商注销申请',
          icon: 'ri-file-copy-line',
          content: [
            '《企业注销登记申请书》',
            '清算报告（全体清算组成员签署）',
            '股东会关于注销的决议',
            '《清税证明》',
            '营业执照正副本',
            '公告报样（如在报纸公告）',
            '其他相关材料'
          ]
        },
        {
          title: '后续处理',
          icon: 'ri-todo-line',
          content: [
            '银行账户注销：到开户银行办理账户注销手续',
            '公章销毁：到公安机关指定地点销毁印章',
            '社保账户注销：到社保部门办理账户注销',
            '其他证照注销：如组织机构代码证等',
            '档案移交：企业档案按规定移交相关部门保管'
          ]
        }
      ]
    }
  };

  // 如果选择了详细指南，显示指南内容
  if (selectedGuide && detailedGuides[selectedGuide as keyof typeof detailedGuides]) {
    const guide = detailedGuides[selectedGuide as keyof typeof detailedGuides];
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        {/* Header */}
        <header className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button 
                  onClick={() => setSelectedGuide(null)}
                  className="text-blue-600 hover:text-blue-800 cursor-pointer"
                >
                  <i className="ri-arrow-left-line text-xl"></i>
                </button>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">{guide.title}</h1>
                  <p className="text-lg text-gray-600 mt-2">详细操作指南和注意事项</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap">
                  收藏指南
                </button>
                <button className="border border-blue-600 text-blue-600 px-6 py-2 rounded-lg hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap">
                  打印指南
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Guide Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Navigation Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-lg p-6 sticky top-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">指南目录</h3>
                <nav className="space-y-2">
                  {guide.sections.map((section, index) => (
                    <a
                      key={index}
                      href={`#section-${index}`}
                      className="flex items-center p-3 text-gray-700 hover:bg-blue-50 hover:text-blue-700 rounded-lg transition-colors cursor-pointer"
                    >
                      <i className={`${section.icon} mr-3 text-blue-600`}></i>
                      <span className="text-sm font-medium">{section.title}</span>
                    </a>
                  ))}
                </nav>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              <div className="space-y-8">
                {guide.sections.map((section, index) => (
                  <div
                    key={index}
                    id={`section-${index}`}
                    className="bg-white rounded-xl shadow-lg overflow-hidden"
                  >
                    <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mr-4">
                          <i className={`${section.icon} text-white text-xl`}></i>
                        </div>
                        <h3 className="text-xl font-bold text-white">{section.title}</h3>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="space-y-4">
                        {section.content.map((item, itemIndex) => (
                          <div key={itemIndex} className="flex items-start">
                            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                            <p className="text-gray-700 leading-relaxed">{item}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="mt-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl p-6 text-white">
                <div className="text-center">
                  <h3 className="text-2xl font-bold mb-4">需要专业协助？</h3>
                  <p className="text-blue-100 mb-6">我们的专业顾问团队随时为您提供一对一指导服务</p>
                  <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                    <button className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
                      在线咨询顾问
                    </button>
                    <button className="border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors cursor-pointer whitespace-nowrap">
                      预约电话回访
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 主页面内容保持不变
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => window.REACT_APP_NAVIGATE('/')}
                className="text-blue-600 hover:text-blue-800 cursor-pointer"
              >
                <i className="ri-arrow-left-line text-xl"></i>
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">企业开办培训</h1>
                <p className="text-lg text-gray-600 mt-2">广州市开办企业一网通平台使用指南</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="bg-blue-100 px-4 py-2 rounded-full">
                <span className="text-blue-800 font-medium">学习进度: 0%</span>
              </div>
              <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap">
                开始学习
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/edac87871975cc99e4e0574c31d3a26c.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-blue-900/70"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            广州市开办企业一网通平台
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            企业开办全流程网上办理，让商事登记更便捷、更高效
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-white">
              <div className="text-2xl font-bold mb-2">1-2天</div>
              <div className="text-blue-100">审核时间</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-white">
              <div className="text-2xl font-bold mb-2">全程网办</div>
              <div className="text-blue-100">办理方式</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-white">
              <div className="text-2xl font-bold mb-2">免费服务</div>
              <div className="text-blue-100">政务服务</div>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Access */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">平台访问方式</h3>
            <p className="text-lg text-gray-600">多渠道便民服务，随时随地办事</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {platformAccess.map((platform) => (
              <div key={platform.type} className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-8">
                <div className="flex items-center mb-6">
                  <div className={`w-16 h-16 ${platform.color} rounded-full flex items-center justify-center mr-4`}>
                    <i className={`${platform.icon} text-white text-2xl`}></i>
                  </div>
                  <div>
                    <h4 className="text-2xl font-bold text-gray-900">{platform.type}</h4>
                    <p className="text-gray-600 mt-1">{platform.description}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  {platform.features.map((feature, index) => (
                    <div key={index} className="flex items-center">
                      <i className="ri-check-line text-green-600 mr-2"></i>
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="bg-blue-50 rounded-xl p-6">
            <div className="flex items-center mb-4">
              <i className="ri-user-line text-blue-600 text-2xl mr-3"></i>
              <h4 className="text-xl font-bold text-gray-900">登录方式</h4>
            </div>
            <p className="text-gray-700">
              办理企业业务通常需使用企业账号、或法定代表人或联络员的个人账号进行实名认证登录
            </p>
          </div>
        </div>
      </section>

      {/* Business Types */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">企业全生命周期服务</h3>
            <p className="text-lg text-gray-600">从开办到注销，全面覆盖企业各阶段办事需求</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {businessTypes.map((business) => (
              <div key={business.id} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden">
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                      <i className={`${business.icon} text-blue-600 text-2xl`}></i>
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-gray-900">{business.title}</h4>
                      <p className="text-gray-600">{business.description}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-2">办理时限</h5>
                      <p className="text-blue-600 font-medium">{business.timeframe}</p>
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-2">主要材料</h5>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {business.materials.slice(0, 2).map((material, index) => (
                          <li key={index}>• {material}</li>
                        ))}
                        {business.materials.length > 2 && (
                          <li className="text-blue-600">• 等{business.materials.length}项材料</li>
                        )}
                      </ul>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <h5 className="font-semibold text-gray-900 mb-2">办理流程</h5>
                    <div className="flex flex-wrap gap-2">
                      {business.process.map((step, index) => (
                        <span 
                          key={index}
                          className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm"
                        >
                          {index + 1}. {step}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => setSelectedGuide(business.id)}
                    className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    查看详细指南
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise Lifecycle Process */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">企业开办完整流程</h3>
            <p className="text-lg text-gray-600">四个关键步骤，确保企业合规运营</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="relative mb-6">
                <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-building-2-line text-white text-3xl"></i>
                </div>
                <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-blue-200 -ml-10"></div>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">1. 工商注册</h4>
              <p className="text-gray-600 mb-4">通过一网通平台办理营业执照</p>
              <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
                时限：1-2个工作日
              </div>
            </div>
            
            <div className="text-center">
              <div className="relative mb-6">
                <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-stamp-line text-white text-3xl"></i>
                </div>
                <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-green-200 -ml-10"></div>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">2. 刻章备案</h4>
              <p className="text-gray-600 mb-4">刻制公章、财务章等必需印章</p>
              <div className="bg-green-50 p-3 rounded-lg text-sm text-green-800">
                时限：1个工作日
              </div>
            </div>
            
            <div className="text-center">
              <div className="relative mb-6">
                <div className="w-20 h-20 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-bank-card-line text-white text-3xl"></i>
                </div>
                <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-purple-200 -ml-10"></div>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">3. 银行开户</h4>
              <p className="text-gray-600 mb-4">开立基本存款账户</p>
              <div className="bg-purple-50 p-3 rounded-lg text-sm text-purple-800">
                时限：3-5个工作日
              </div>
            </div>
            
            <div className="text-center">
              <div className="relative mb-6">
                <div className="w-20 h-20 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-file-list-3-line text-white text-3xl"></i>
                </div>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">4. 税务报到</h4>
              <p className="text-gray-600 mb-4">办理税务登记，核定税种</p>
              <div className="bg-orange-50 p-3 rounded-lg text-sm text-orange-800">
                时限：1-3个工作日
              </div>
            </div>
          </div>
          
          <div className="mt-12 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-8">
            <div className="text-center mb-6">
              <h4 className="text-2xl font-bold text-gray-900 mb-2">完整办理时限</h4>
              <p className="text-lg text-gray-600">从工商注册到税务报到，总计约 <span className="text-blue-600 font-bold">7-11个工作日</span></p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-4 rounded-lg shadow-sm text-center">
                <i className="ri-time-line text-blue-600 text-2xl mb-2"></i>
                <h5 className="font-semibold text-gray-900">最快时限</h5>
                <p className="text-blue-600 font-bold">6个工作日</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm text-center">
                <i className="ri-calendar-line text-green-600 text-2xl mb-2"></i>
                <h5 className="font-semibold text-gray-900">一般时限</h5>
                <p className="text-green-600 font-bold">7-11个工作日</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm text-center">
                <i className="ri-money-dollar-circle-line text-purple-600 text-2xl mb-2"></i>
                <h5 className="font-semibold text-gray-900">总体费用</h5>
                <p className="text-purple-600 font-bold">800-2000元</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Process - Enterprise Registration */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">企业开办详细流程</h3>
            <p className="text-lg text-gray-600">一步步指导，确保办理顺利</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h4 className="text-2xl font-bold text-gray-900 mb-6">所需材料清单</h4>
              <div className="space-y-6">
                <div className="bg-blue-50 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center mr-3">
                      <i className="ri-user-line text-white"></i>
                    </div>
                    <h5 className="text-lg font-semibold text-gray-900">人员信息</h5>
                  </div>
                  <ul className="space-y-2 text-gray-700">
                    <li>• 所有股东身份证正反面照片及联系方式</li>
                    <li>• 法定代表人身份证正反面照片及联系方式</li>
                    <li>• 执行董事/经理身份证正反面照片及联系方式</li>
                    <li>• 财务负责人身份证正反面照片及联系方式</li>
                    <li>• 联络员身份证正反面照片及联系方式</li>
                  </ul>
                </div>
                
                <div className="bg-green-50 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center mr-3">
                      <i className="ri-home-line text-white"></i>
                    </div>
                    <h5 className="text-lg font-semibold text-gray-900">注册地址证明</h5>
                  </div>
                  <ul className="space-y-2 text-gray-700">
                    <li>• 租赁合同（需房东签字盖章）</li>
                    <li>• 房产证复印件</li>
                    <li>• 房东身份证复印件</li>
                    <li>• 场地使用证明（如适用）</li>
                  </ul>
                </div>
                
                <div className="bg-purple-50 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center mr-3">
                      <i className="ri-file-text-line text-white"></i>
                    </div>
                    <h5 className="text-lg font-semibold text-gray-900">企业基本信息</h5>
                  </div>
                  <ul className="space-y-2 text-gray-700">
                    <li>• 公司名称（准备3-5个备选名称）</li>
                    <li>• 注册资本金额</li>
                    <li>• 经营范围（主营业务和兼营业务）</li>
                    <li>• 股东出资比例</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="text-2xl font-bold text-gray-900 mb-6">办理流程步骤</h4>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mr-4 text-white font-bold">1</div>
                  <div className="flex-1">
                    <h5 className="text-lg font-semibold text-gray-900 mb-2">实名登录一网通平台</h5>
                    <p className="text-gray-600 mb-2">使用法定代表人或联络员账号进行实名认证登录</p>
                    <div className="bg-yellow-50 p-3 rounded-lg">
                      <p className="text-sm text-yellow-800">💡 提示：首次使用需要进行身份认证，准备好身份证和手机号</p>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mr-4 text-white font-bold">2</div>
                  <div className="flex-1">
                    <h5 className="text-lg font-semibold text-gray-900 mb-2">填写企业基本信息</h5>
                    <p className="text-gray-600 mb-2">按照系统提示逐步填写企业名称、注册资本、经营范围等信息</p>
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <p className="text-sm text-blue-800">📋 注意：信息填写要准确无误，后期变更较为繁琐</p>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mr-4 text-white font-bold">3</div>
                  <div className="flex-1">
                    <h5 className="text-lg font-semibold text-gray-900 mb-2">上传所需电子材料</h5>
                    <p className="text-gray-600 mb-2">按要求上传身份证、地址证明等材料的清晰电子版</p>
                    <div className="bg-green-50 p-3 rounded-lg">
                      <p className="text-sm text-green-800">📸 要求：图片清晰可见，文件大小不超过5MB</p>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mr-4 text-white font-bold">4</div>
                  <div className="flex-1">
                    <h5 className="text-lg font-semibold text-gray-900 mb-2">相关人员电子签名</h5>
                    <p className="text-gray-600 mb-2">股东、法定代表人等相关人员通过手机扫码进行电子签名</p>
                    <div className="bg-purple-50 p-3 rounded-lg">
                      <p className="text-sm text-purple-800">✍️ 提醒：签名需本人操作，不可代签</p>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mr-4 text-white font-bold">5</div>
                  <div className="flex-1">
                    <h5 className="text-lg font-semibold text-gray-900 mb-2">提交申请等待审核</h5>
                    <p className="text-gray-600 mb-2">系统自动提交至相关部门审核，通常1-2个工作日完成</p>
                    <div className="bg-orange-50 p-3 rounded-lg">
                      <p className="text-sm text-orange-800">⏰ 时效：工作日提交当天开始计算审核时间</p>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mr-4 text-white font-bold">6</div>
                  <div className="flex-1">
                    <h5 className="text-lg font-semibold text-gray-900 mb-2">领取营业执照和印章</h5>
                    <p className="text-gray-600 mb-2">审核通过后到指定政务服务中心领取或选择邮寄服务</p>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-sm text-gray-800">📍 地点：如荔湾区政务服务中心（逢源路128号金升大厦二楼）</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Points */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">重要提示与注意事项</h3>
            <p className="text-lg text-gray-600">掌握关键要点，避免常见问题</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {keyPoints.map((point, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mr-4">
                    <i className={`${point.icon} ${point.color} text-xl`}></i>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900">{point.title}</h4>
                </div>
                <p className="text-gray-600">{point.description}</p>
              </div>
            ))}
          </div>
          
          <div className="mt-8 bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
            <div className="flex items-center mb-2">
              <i className="ri-error-warning-line text-red-600 text-xl mr-2"></i>
              <h4 className="text-lg font-semibold text-red-800">特别提醒</h4>
            </div>
            <p className="text-red-700">
              广州市开办企业一网通平台旨在让商事登记更便捷。对于大多数常规业务，都可以选择线上办理，省时省力。
              如遇到特殊情况或复杂业务，建议及时咨询专业机构或前往现场办理。
            </p>
          </div>
        </div>
      </section>

      {/* Contact Support */}
      <section className="py-16 bg-blue-600">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl font-bold text-white mb-4">
            需要专业指导？
          </h3>
          <p className="text-xl text-blue-100 mb-8">
            我们的专业客服团队随时为您提供咨询服务
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <button className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
              在线咨询
            </button>
            <button className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors cursor-pointer whitespace-nowrap">
              电话咨询: 020-1234-5678
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h4 className="text-lg font-semibold mb-4">联系我们</h4>
              <div className="space-y-2 text-gray-300">
                <p>电话: 020-1234-5678</p>
                <p>邮箱: training@example.com</p>
                <p>地址: 广州市天河区xxx路123号</p>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">快速链接</h4>
              <div className="space-y-2 text-gray-300">
                <p className="cursor-pointer hover:text-white" onClick={() => window.REACT_APP_NAVIGATE('/')}>返回首页</p>
                <p>培训报名</p>
                <p>学习资料</p>
                <p>常见问题</p>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">政务平台</h4>
              <div className="space-y-2 text-gray-300">
                <p>广州市开办企业一网通平台</p>
                <p>广州市政务服务网</p>
                <p>广东省政务服务网</p>
                <p>国家企业信用信息公示系统</p>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">企业服务</h4>
              <div className="space-y-2 text-gray-300">
                <p>企业开办服务</p>
                <p>刻章备案服务</p>
                <p>银行开户指导</p>
                <p>税务报到协助</p>
                <p><a href="https://readdy.ai/?origin=logo" className="hover:text-white transition-colors">Made with Readdy</a></p>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 企服培训平台. 保留所有权利.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
